/**
 * API client wrapper.
 *
 * All HTTP calls to the FastAPI backend go through this module.
 * Automatically attaches the Firebase ID token to every request.
 */

import { auth, isStubAuthEnabled } from "@/lib/firebase";

function normalizeApiBaseUrl(raw: string | undefined): string {
  const input = (raw ?? "/api/v1").trim();
  const withNoTrailingSlash = input.replace(/\/+$/, "");
  const requiredSuffix = "/api/v1";

  // Relative base (e.g. "/api/v1")
  if (withNoTrailingSlash.startsWith("/")) {
    return withNoTrailingSlash.endsWith(requiredSuffix)
      ? withNoTrailingSlash
      : `${withNoTrailingSlash}${requiredSuffix}`;
  }

  // Absolute base (e.g. "https://acervo.galerialeme.com")
  try {
    const url = new URL(withNoTrailingSlash);
    const normalizedPath = url.pathname.replace(/\/+$/, "");
    if (!normalizedPath.endsWith(requiredSuffix)) {
      url.pathname = `${normalizedPath}${requiredSuffix}`;
    }
    return url.toString().replace(/\/+$/, "");
  } catch {
    // Fallback: keep prior behavior for malformed values.
    return withNoTrailingSlash;
  }
}

const BASE_URL = normalizeApiBaseUrl(import.meta.env.VITE_API_URL);
const REQUEST_TIMEOUT_MS = 15_000;
const TOKEN_TIMEOUT_MS = 8_000;
// Uploads (imagem/vídeo até 150 MB) precisam de MUITO mais que os 15s padrão:
// o cliente envia o arquivo e ainda espera o backend subir para o GCS antes
// de responder. 15s abortava qualquer vídeo.
const UPLOAD_TIMEOUT_MS = 300_000; // 5 min

type ApiErrorPayload = {
  detail?: unknown;
  message?: unknown;
  errors?: Record<string, string[]>;
};

function formatUnknownError(value: unknown): string | null {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (!value || typeof value !== "object") return null;

  if (Array.isArray(value)) {
    const messages = value
      .map((entry) => {
        if (typeof entry === "string") return entry;
        if (!entry || typeof entry !== "object") return null;

        const record = entry as Record<string, unknown>;
        const msg = formatUnknownError(record.msg ?? record.message ?? record.detail);
        const loc = Array.isArray(record.loc)
          ? record.loc.filter((part) => part !== "body").join(".")
          : null;

        return [loc, msg].filter(Boolean).join(": ");
      })
      .filter(Boolean);

    return messages.length > 0 ? messages.join("; ") : null;
  }

  const record = value as Record<string, unknown>;
  return (
    formatUnknownError(record.msg) ??
    formatUnknownError(record.message) ??
    formatUnknownError(record.detail)
  );
}

function formatApiErrorPayload(payload: ApiErrorPayload, fallback: string): string {
  return (
    formatUnknownError(payload.detail) ??
    formatUnknownError(payload.message) ??
    fallback
  );
}

async function getIdToken(): Promise<string> {
  return getIdTokenWithOptions();
}

async function getIdTokenWithOptions(options?: { forceRefresh?: boolean }): Promise<string> {
  if (isStubAuthEnabled) {
    const email = sessionStorage.getItem("stub_user_email");
    if (!email) {
      throw new ApiError(401, "Not authenticated");
    }
    return `stub:${email}`;
  }
  if (!auth) {
    throw new ApiError(503, "Authentication is not configured");
  }
  let user = auth.currentUser;
  if (!user) {
    const authWithReady = auth as typeof auth & {
      authStateReady?: () => Promise<void>;
    };
    if (typeof authWithReady.authStateReady === "function") {
      await authWithReady.authStateReady();
      user = auth.currentUser;
    }
  }
  if (!user) {
    throw new ApiError(401, "Not authenticated");
  }
  return user.getIdToken(options?.forceRefresh === true);
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
    public readonly errors?: Record<string, string[]>
  ) {
    super(detail);
    this.name = "ApiError";
  }
}

function raceWithAuthTimeout<T>(promise: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new ApiError(504, "Autenticação demorou demais. Tente novamente.")),
      TOKEN_TIMEOUT_MS
    );
    promise.then(
      (value) => { window.clearTimeout(timer); resolve(value); },
      (err)   => { window.clearTimeout(timer); reject(err); }
    );
  });
}

async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit,
  timeoutMessage: string,
  timeoutMs: number = REQUEST_TIMEOUT_MS
): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(input, {
      ...init,
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError(504, timeoutMessage);
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

function buildUrl(path: string, params?: Record<string, unknown>): string {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, String(value));
      }
    });
  }
  return url.toString();
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: { params?: Record<string, unknown>; headers?: HeadersInit; timeoutMs?: number }
): Promise<T> {
  const url = buildUrl(path, options?.params);
  const buildHeaders = (token: string): HeadersInit => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...options?.headers,
  });

  const doFetch = async (token: string) =>
    fetchWithTimeout(
      url,
      {
      method,
      headers: buildHeaders(token),
      body: body != null ? JSON.stringify(body) : undefined,
      },
      "A API demorou demais para responder. Tente novamente.",
      options?.timeoutMs
    );

  const token = await raceWithAuthTimeout(getIdToken());
  let response = await doFetch(token);

  if (
    response.status === 401 &&
    auth?.currentUser &&
    !isStubAuthEnabled
  ) {
    const refreshedToken = await raceWithAuthTimeout(getIdTokenWithOptions({ forceRefresh: true }));
    response = await doFetch(refreshedToken);
  }

  if (!response.ok) {
    const error: ApiErrorPayload = await response
      .json()
      .catch(() => ({ detail: response.statusText }));

    // Only force logout on confirmed unauthenticated responses.
    if (response.status === 401) {
      if (auth) await auth.signOut();
      else if (isStubAuthEnabled) sessionStorage.removeItem("stub_user_email");
      window.location.href = "/login";
    }

    throw new ApiError(
      response.status,
      formatApiErrorPayload(error, response.statusText || "Unknown error"),
      error.errors
    );
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

async function publicRequest<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: { params?: Record<string, unknown>; headers?: HeadersInit }
): Promise<T> {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...options?.headers,
  };

  const response = await fetchWithTimeout(
    buildUrl(path, options?.params),
    {
      method,
      headers,
      body: body != null ? JSON.stringify(body) : undefined,
    },
    "A API pública demorou demais para responder. Tente novamente."
  );

  if (!response.ok) {
    const error: ApiErrorPayload = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new ApiError(
      response.status,
      formatApiErrorPayload(error, response.statusText || "Unknown error"),
      error.errors
    );
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

async function downloadBlob(path: string, fallbackFilename: string): Promise<void> {
  const token = await raceWithAuthTimeout(getIdToken());
  const response = await fetchWithTimeout(
    buildUrl(path),
    {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    },
    "O download demorou demais para responder. Tente novamente."
  );
  if (!response.ok) {
    const error: ApiErrorPayload = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new ApiError(
      response.status,
      formatApiErrorPayload(error, response.statusText || "Download failed")
    );
  }

  // Prefer filename from Content-Disposition when available
  const cd = response.headers.get("content-disposition") ?? "";
  let filename = fallbackFilename;
  const utf8 = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const plain = cd.match(/filename="?([^";]+)"?/i);
  if (utf8) filename = decodeURIComponent(utf8[1]);
  else if (plain) filename = plain[1];

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}


async function upload<T>(
  path: string,
  file: File,
  extraData?: Record<string, string>
): Promise<T> {
  const token = await raceWithAuthTimeout(getIdToken());

  const formData = new FormData();
  formData.append("file", file);
  if (extraData) {
    Object.entries(extraData).forEach(([key, val]) =>
      formData.append(key, val)
    );
  }

  const response = await fetchWithTimeout(
    buildUrl(path),
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    },
    "O upload demorou demais para responder. Tente novamente.",
    UPLOAD_TIMEOUT_MS
  );

  if (!response.ok) {
    const error: ApiErrorPayload = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new ApiError(
      response.status,
      formatApiErrorPayload(error, response.statusText || "Upload failed")
    );
  }

  return response.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string, params?: Record<string, unknown>) =>
    request<T>("GET", path, undefined, { params }),

  post: <T>(path: string, body?: unknown, options?: { timeoutMs?: number }) =>
    request<T>("POST", path, body, options),

  patch: <T>(path: string, body?: unknown) =>
    request<T>("PATCH", path, body),

  put: <T>(path: string, body?: unknown) =>
    request<T>("PUT", path, body),

  delete: <T = void>(path: string) =>
    request<T>("DELETE", path),

  upload,
  downloadBlob,
  publicGet: <T>(path: string, params?: Record<string, unknown>) =>
    publicRequest<T>("GET", path, undefined, { params }),
  publicPost: <T>(path: string, body?: unknown, params?: Record<string, unknown>) =>
    publicRequest<T>("POST", path, body, { params }),
};
